import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import { useExportOptions } from '@/hooks/useExportOptions';
import type { TablePropsWithActions } from '@/utils/table/types';

import { buildControlGroupRegisterToolbarConfig } from './registerToolbarConfig';
import type { ControlGroupTableFields } from './types';

interface Props {
  title: string;
  /** Raw (unparenthesised) count — the toolbar wraps it itself. */
  counter?: string;
  tableProps: TablePropsWithActions<ControlGroupTableFields>;
  /** Opens the create-control-group modal; gated `insert:control_group`. */
  onCreate: () => void;
}

/**
 * Object toolbar for the Control Groups register, mounted only when the
 * `object_toolbar` flag is on. Unlike the Controls/Indicators register toolbars
 * there is no `CustomisableRibbon` on this page, so no ribbon-edit overflow — but
 * it carries a primary "Add" action those registers lack (CUS-153 sweep). Owning
 * `useExportOptions` here keeps it off the legacy `ExportButton` path.
 */
export const ControlGroupRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  onCreate,
}) => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'controlGroups' });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<ControlGroupTableFields>({
      tableProps,
      entityLabel: title,
      onShowCustomPdfModal,
    });

  const toolbarConfig = useMemo(
    () =>
      buildControlGroupRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        // The toolbar resolves `insert:control_group` type-level (no
        // parentObject on a register toolbar) and hides the action when denied —
        // exactly replicating the legacy <Permission insert:control_group> gate.
        create: {
          label: st('create_new_button', 'Add'),
          onClick: onCreate,
        },
      }),
    [title, counter, exportOptions, onCreate, st]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={false}
      />
    </>
  );
};
